import React, { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import ExchangesList from './components/ExchangesList';
import { Spinner } from '../../../components/Spinner';
import { useExchangeViewModel } from './viewmodel/exchange-viewmodel';
import { ToastContainer, toast } from 'react-toastify'; // Add toast here
import 'react-toastify/dist/ReactToastify.css';
import { fetchExchangeById } from './controller/exchange-controller'; // Import the function to fetch exchange by ID
// Import the new ExchangeExportPdf component
import { ExchangeExportMultiple } from './components/ExchangeExportPdf';

// Import the exact components from OrdersPage
import StatusSummary from './components/StatusSummary';
import OrdersHeader from '../orders-components/OrdersHeader';
import OrderFilters from '../orders-components/OrderFilters';
import { PrintExchange, BatchPrintExchanges } from './components/PrintExchange';

// Update imports to include the new controller functions
import {
  syncronizeExchanges,
  trackExchanges
} from './controller/exchange-controller';
import { usePermissionViewModel } from '../permissions/viewmodel/permission-viewmodel';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const ExchangesPage = () => {
  // State for selected exchanges (same pattern as OrdersPage)
  const [selectedExchanges, setSelectedExchanges] = useState([]);

  const {
    exchanges,
    isLoading,
    statusSummary,
    pagination,
    activeTab,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    syncroFilter,
    setSyncroFilter,
    handleTabChange,
    handlePageChange,
    handlePageSizeChange,
    handleStatusFilterChange,
    applyFilters,
    fetchHeaderData,
    refreshExchangeData,
    refreshStatusSummary
  } = useExchangeViewModel();

    const {hasPermission} = usePermissionViewModel('echange')

  useEffect(() => {
    console.log('Exchanges from viewmodel:', exchanges);
  }, [exchanges]);

  // Handler for exchange selection
  const handleExchangeSelection = (exchangeId, exchangeStatus) => {
    setSelectedExchanges(prev => {
      if (prev.some(item => item.id === exchangeId)) {
        return prev.filter(item => item.id !== exchangeId);
      } else {
        return [...prev, { id: exchangeId, status: exchangeStatus }];
      }
    });
  };

  // Define these functions that OrdersHeader expects
  const exportToExcel = () => {
    console.log('Export to Excel called');
    // Implement your export logic here
  };

  // Function to sync exchanges with external systems
  const handleSyncExchanges = async (mode, items) => {
    try {
      // Determine which exchanges to synchronize
      const exchangesToSync = mode === 'selected'
        ? selectedExchanges
        : exchanges.filter(exchange => {
          // Filter eligible exchanges by status if needed
          return true; // Change this if you need to filter by status
        });

      // Extract the IDs
      const exchangeIds = exchangesToSync.map(exchange => exchange.id);

      if (exchangeIds.length === 0) {
        toast.warning(mode === 'selected'
          ? "Aucun échange sélectionné à synchroniser"
          : "Aucun échange éligible à synchroniser");
        return;
      }

      toast.info(`Synchronisation de ${exchangeIds.length} échange(s) en cours...`);

      // Call the controller function
      await syncronizeExchanges(exchangeIds);

      // Handle successful response
      toast.success(`${exchangeIds.length} échange(s) synchronisé(s) avec succès!`);

      // Clear selection if needed
      if (mode === 'selected') {
        setSelectedExchanges([]);
      }

      // Refresh data
      refreshExchangeData();
      refreshStatusSummary();

    } catch (error) {
      console.error('Error synchronizing exchanges:', error);
      toast.error(`Erreur lors de la synchronisation: ${error.response?.data?.message || error.message}`);
    }
  };

  // Function to track exchanges
  const handleTrackExchanges = async (mode, items) => {
    try {
      // Determine which exchanges to track
      const exchangesToTrack = mode === 'selected'
        ? selectedExchanges
        : exchanges;

      // Extract the IDs
      const exchangeIds = exchangesToTrack.map(exchange => exchange.id);

      if (exchangeIds.length === 0) {
        toast.warning(mode === 'selected'
          ? "Aucun échange sélectionné pour le suivi"
          : "Aucun échange disponible pour le suivi");
        return;
      }

      toast.info(`Mise à jour du suivi pour ${exchangeIds.length} échange(s) en cours...`);

      // Call the controller function
      await trackExchanges(exchangeIds);

      // Handle successful response
      toast.success(`Suivi mis à jour pour ${exchangeIds.length} échange(s)!`);

      // Clear selection if needed
      if (mode === 'selected') {
        setSelectedExchanges([]);
      }

      // Refresh data
      refreshExchangeData();
      refreshStatusSummary();

    } catch (error) {
      console.error('Error tracking exchanges:', error);
      toast.error(`Erreur lors de la mise à jour du suivi: ${error.response?.data?.message || error.message}`);
    }
  };

  // Status options for filters
  const statusOptions = [
    { value: 'all', label: 'Tous les statuts', color: 'gray' },
    { value: 'pending', label: 'En attente', color: 'yellow' },
    { value: 'processing', label: 'En traitement', color: 'blue' },
    { value: 'completed', label: 'Terminé', color: 'green' },
    { value: 'cancelled', label: 'Annulé', color: 'red' }
  ];

  // Add state for printing functionality
  const [printingExchange, setPrintingExchange] = useState(null);
  const [batchPrinting, setBatchPrinting] = useState(false);

  // Define the print function
  const handlePrintExchange = (exchangeId) => {
    console.log('Printing exchange with ID:', exchangeId);
    setPrintingExchange(exchangeId);

    // Give more time for the print component to render
    setTimeout(() => {
      console.log('Triggering print for exchange');
      window.print();
      setPrintingExchange(null);
    }, 1000); // Increased timeout to 1000ms
  };

  // Define batch print function
  const handleBatchPrintExchanges = () => {
    if (selectedExchanges.length === 0) {
      toast.warn("Veuillez sélectionner au moins un échange à imprimer");
      return;
    }

    console.log('Batch printing exchanges:', selectedExchanges);
    setBatchPrinting(true);

    // Give more time for the print component to render
    setTimeout(() => {
      console.log('Triggering batch print');
      window.print();
      setBatchPrinting(false);
    }, 1000); // Increased timeout to 1000ms
  };

  // Function to be passed to OrdersHeader for printing
  // Updated handlePrintOrders function for ExchangesPage.jsx

  // Update the handlePrintOrders function to fetch original order data
  const handlePrintOrders = async (mode, items) => {
    try {
      // Determine which exchanges to print
      const exchangesToPrint = mode === 'selected'
        ? selectedExchanges
        : exchanges;

      if (!exchangesToPrint || exchangesToPrint.length === 0) {
        toast.warning("Aucun échange à imprimer");
        return;
      }

      console.log('Printing exchanges:', exchangesToPrint);

      // Create a new window for printing
      const printWindow = window.open('', '_blank');

      if (!printWindow) {
        toast.error("Veuillez autoriser les fenêtres pop-up pour l'impression");
        return;
      }

      // Show loading state in print window
      printWindow.document.write(`
      <html>
        <head>
          <title>Impression des échanges</title>
          <style>
            body {
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
              font-family: Arial, sans-serif;
            }
            .loader {
              border: 5px solid #f3f3f3;
              border-top: 5px solid #3498db;
              border-radius: 50%;
              width: 50px;
              height: 50px;
              animation: spin 1s linear infinite;
              margin-bottom: 20px;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <div style="text-align: center;">
            <div class="loader"></div>
            <h2>Préparation des échanges pour l'impression...</h2>
          </div>
        </body>
      </html>
    `);

      // Fetch and prepare exchange data with original order details
      // Updated preparedExchanges construction to normalize exchange data format
      const preparedExchanges = await Promise.all(exchangesToPrint.map(async (exchange) => {
        try {
          // Fetch complete exchange data if needed
          let exchangeData = exchange;

          // If we only have the ID, fetch the full exchange
          if (exchange.id && !exchange.items && !exchange.entet) {
            try {
              const exchangeResponse = await fetchExchangeById(exchange.id);
              if (!exchangeResponse) throw new Error(`Exchange not found: ${exchange.id}`);

              // Check if the API returned entet/line format or flattened format with items
              exchangeData = exchangeResponse;
              console.log('Fetched exchange data:', exchangeData);
            } catch (error) {
              console.error(`Failed to fetch exchange ${exchange.id}:`, error);
              return null;
            }
          }

          // Get original order ID from appropriate location in the data structure
          const orderId = exchangeData.entet?.bonCommandeId || exchangeData.bonCommandeId;

          if (!orderId) {
            console.error('No orderId found for exchange:', exchangeData);
            // Return exchange data with placeholder client info
            return {
              entet: exchangeData.entet || exchangeData,
              line: exchangeData.line || exchangeData.items || [],
              originalOrder: {
                clientName: "Client inconnu",
                clientPhone: "N/A",
                ville: "TUN",
                clientAdresse: "Adresse non disponible"
              }
            };
          }

          try {
            // Fetch the original order data to get client info
            const orderResponse = await fetch(`/api/orders/${orderId}`);

            if (!orderResponse.ok) {
              throw new Error(`Order not found: ${orderId}`);
            }

            const orderData = await orderResponse.json();

            // Now construct a properly formatted exchange object that works with our component
            // Transform the data into the format our component expects
            let formattedExchange;

            if (exchangeData.entet) {
              // Already in nested format
              formattedExchange = {
                entet: exchangeData.entet,
                line: exchangeData.line || [],
                originalOrder: {
                  clientName: `${orderData.client?.prenom || ''} ${orderData.client?.nom || 'Client'}`.trim(),
                  clientPhone: orderData.client?.tel || "N/A",
                  ville: orderData.ville || "TUN",
                  clientAdresse: orderData.adresseLivraison || "Adresse non disponible"
                }
              };
            } else {
              // Flat format, convert to nested format
              formattedExchange = {
                entet: {
                  id: exchangeData.id,
                  reference: exchangeData.reference,
                  bonCommandeId: exchangeData.bonCommandeId,
                  dateEchange: exchangeData.dateEchange,
                  description: exchangeData.description,
                  totalTTC: exchangeData.totalTTC,
                  totalHt: exchangeData.totalHt,
                  totalTva: exchangeData.totalTva,
                  fraisDeLivraison: exchangeData.fraisDeLivraison,
                  totalRemise: exchangeData.totalRemise,
                  resteAPayer: exchangeData.resteAPayer,
                  codeLivraison: exchangeData.codeLivraison,
                  etat: exchangeData.etat
                },
                line: exchangeData.items || [],
                originalOrder: {
                  clientName: `${orderData.client?.prenom || ''} ${orderData.client?.nom || 'Client'}`.trim(),
                  clientPhone: orderData.client?.tel || "N/A",
                  ville: orderData.ville || "TUN",
                  clientAdresse: orderData.adresseLivraison || "Adresse non disponible"
                }
              };
            }

            console.log('Formatted exchange for printing:', formattedExchange);
            return formattedExchange;

          } catch (orderError) {
            console.error(`Failed to fetch original order ${orderId}:`, orderError);
            // Return with default values if order fetch fails
            return {
              entet: exchangeData.entet || exchangeData,
              line: exchangeData.line || exchangeData.items || [],
              originalOrder: {
                clientName: "Client inconnu",
                clientPhone: "N/A",
                ville: "TUN",
                clientAdresse: "Adresse non disponible"
              }
            };
          }
        } catch (error) {
          console.error(`Error preparing exchange:`, error);
          return null;
        }
      }));

      // Filter out any null values from failed fetches
      const validExchanges = preparedExchanges.filter(Boolean);

      if (validExchanges.length === 0) {
        printWindow.close();
        toast.error("Aucun échange valide à imprimer");
        return;
      }

      console.log('Prepared exchanges for printing:', validExchanges);

      // Create a temporary container to render the PDF export component
      const tempContainer = document.createElement('div');
      document.body.appendChild(tempContainer);

      // Render the ExchangeExportMultiple component to the temp container
      const ReactDOM = require('react-dom');

      ReactDOM.render(
        <ExchangeExportMultiple
          exchanges={validExchanges}
          logo="/logo-print.png"
          mylerzLogo="/mylerz.png"
        />,
        tempContainer
      );

      // Wait a short time for rendering to complete
      setTimeout(() => {
        try {
          // Replace the content in the print window
          const printContent = tempContainer.innerHTML;
          printWindow.document.open();
          printWindow.document.write(`
          <html>
            <head>
              <title>Échanges - Impression</title>
              <style>
                @page {
                  size: A5 landscape;
                  margin: 0;
                }
                body {
                  margin: 0;
                  padding: 0;
                }
              </style>
            </head>
            <body>
              ${printContent}
              <script>
                window.onload = function() {
                  setTimeout(function() {
                    window.print();
                  }, 500);
                }
              </script>
            </body>
          </html>
        `);
          printWindow.document.close();

          // Clean up the temporary container
          document.body.removeChild(tempContainer);

          // Show success message
          toast.success(`${validExchanges.length} échange(s) prêt(s) à imprimer`);

          // Clear selections if needed
          if (mode === 'selected') {
            setSelectedExchanges([]);
          }

        } catch (error) {
          console.error('Error rendering exchanges for print:', error);
          printWindow.close();
          document.body.removeChild(tempContainer);
          toast.error(`Erreur lors de la préparation de l'impression: ${error.message}`);
        }
      }, 1000); // Give time for the components to render

    } catch (error) {
      console.error('Error in print handler:', error);
      toast.error(`Erreur: ${error.message}`);
    }
  };

  // God-level design updates focusing just on the table and header
  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50">
      {/* Enhanced header section */}
      <div className="mb-6">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-3xl font-extrabold bg-clip-text sm:text-4xl sm:truncate inline-flex items-center">
              Échanges
              <span className="ml-3 text-sm font-medium text-gray-600 bg-gray-100 px-2.5 py-0.5 rounded-full">
                {exchanges?.length || 0} total
              </span>
            </h2>
          </div>
{    hasPermission("write") &&     <div className="mt-4 flex sm:mt-0 sm:ml-4 gap-3">
            <a
              href="/admin/exchanges/add"
              className="relative overflow-hidden inline-flex items-center px-5 py-2.5 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
            >
              <span className="absolute inset-0 overflow-hidden rounded-lg">
                <span className="absolute inset-0 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></span>
              </span>
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              <span className="relative">Nouvel Échange</span>
            </a>
          </div>}
        </div>
      </div>

      {/* Keep your existing OrderFilters component */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6 border border-gray-200">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50">
          <h3 className="text-base font-medium text-gray-900">Filtres & Recherche</h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <OrderFilters
            searchTerm={searchTerm || ''}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter || 'all'}
            setStatusFilter={setStatusFilter}
            statusOptions={statusOptions}
            onApplyFilters={applyFilters}
            startDate={startDate || ''}
            setStartDate={setStartDate}
            endDate={endDate || ''}
            setEndDate={setEndDate}
            syncroFilter={syncroFilter || 'all'}
            setSyncroFilter={setSyncroFilter}
          />
        </div>
      </div>

      {/* Premium table design */}
      <div className="rounded-xl shadow-sm overflow-hidden bg-white border border-gray-200">
        {/* Enhanced Table Header with Tab Pills */}
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
          </div>
        </div>

        {/* Batch selection alert bar */}
        {selectedExchanges.length > 0 && (
          <div className="bg-indigo-50 px-6 py-2.5 border-b border-indigo-100 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="flex items-center justify-center bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full h-5 w-5">{selectedExchanges.length}</span>
              <span className="text-sm font-medium text-indigo-800">Échanges sélectionnés</span>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePrintOrders('selected')}
                className="text-xs font-medium text-indigo-700 hover:text-indigo-900 flex items-center"
              >
                <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
                </svg>
                Imprimer
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => setSelectedExchanges([])}
                className="text-xs font-medium text-indigo-700 hover:text-indigo-900 flex items-center"
              >
                <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Désélectionner
              </button>
            </div>
          </div>
        )}

        {/* Table Panel Content - Keep your existing structure but enhance styling */}
        <Tab.Group selectedIndex={activeTab} onChange={handleTabChange}>
          <Tab.Panels>
            {[0, 1, 2, 3].map((tabIndex) => (
              <Tab.Panel key={tabIndex} className="focus:outline-none">
                {isLoading ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="relative">
                      <div className="h-12 w-12 rounded-full border-t-2 border-b-2 border-indigo-500 animate-spin"></div>
                      <div className="h-12 w-12 rounded-full border-r-2 border-l-2 border-transparent border-opacity-50 absolute top-0 animate-pulse"></div>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    {/* Custom styling for ExchangesList - the component will be rendered here */}
                    <style jsx global>{`
                    /* Enhanced table styles */
                    .exchanges-table {
                      width: 100%;
                      border-collapse: separate;
                      border-spacing: 0;
                    }
                    
                    .exchanges-table th {
                      background-color: #f9fafb;
                      font-weight: 600;
                      text-transform: uppercase;
                      letter-spacing: 0.05em;
                      color: #4b5563;
                      font-size: 0.75rem;
                      padding: 0.75rem 1rem;
                      text-align: left;
                      border-bottom: 1px solid #e5e7eb;
                    }
                    
                    .exchanges-table tr {
                      transition: all 0.15s ease;
                    }
                    
                    .exchanges-table tr:hover {
                      background-color: #f3f4f6;
                    }
                    
                    .exchanges-table td {
                      padding: 1rem;
                      vertical-align: middle;
                      border-bottom: 1px solid #e5e7eb;
                      color: #1f2937;
                    }
                    
                    .exchanges-table tbody tr:last-child td {
                      border-bottom: none;
                    }
                    
                    /* Status pill styling */
                    .status-pill {
                      display: inline-flex;
                      align-items: center;
                      padding: 0.125rem 0.625rem;
                      background: white;
                      border-radius: 9999px;
                      font-size: 0.75rem;
                      font-weight: 500;
                    }
                    
                    .status-pill:before {
                      content: '';
                      display: block;
                      width: 0.5rem;
                      height: 0.5rem;
                      border-radius: 9999px;
                      margin-right: 0.375rem;
                    }
                    
                    .status-pill.pending {
                      color: #92400e;
                      background-color: #fef3c7;
                    }
                    
                    .status-pill.pending:before {
                      background-color: #f59e0b;
                    }
                    
                    .status-pill.processing {
                      color: #1e40af;
                      background-color: #dbeafe;
                    }
                    
                    .status-pill.processing:before {
                      background-color: #3b82f6;
                    }
                    
                    .status-pill.completed {
                      color: #065f46;
                      background-color: #d1fae5;
                    }
                    
                    .status-pill.completed:before {
                      background-color: #10b981;
                    }
                    
                    .status-pill.cancelled {
                      color: #991b1b;
                      background-color: #fee2e2;
                    }
                    
                    .status-pill.cancelled:before {
                      background-color: #ef4444;
                    }
                    
                    /* Action buttons styling */
                    .action-button {
                      color: #4b5563;
                      padding: 0.375rem;
                      border-radius: 0.375rem;
                      transition: all 0.15s ease;
                    }
                    
                    .action-button:hover {
                      color: #1f2937;
                      background-color: #f3f4f6;
                    }
                    
                    /* Checkbox styling */
                    .custom-checkbox {
                      appearance: none;
                      padding: 0;
                      color: #6b7280;
                      background-color: #fff;
                      border: 1px solid #d1d5db;
                      border-radius: 0.25rem;
                      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                      width: 1rem;
                      height: 1rem;
                      display: inline-block;
                      vertical-align: middle;
                      position: relative;
                      cursor: pointer;
                    }
                    
                    .custom-checkbox:checked {
                      background-color: #6366f1;
                      border-color: #6366f1;
                    }
                    
                    .custom-checkbox:checked:after {
                      content: '';
                      display: block;
                      position: absolute;
                      top: 0.15rem;
                      left: 0.35rem;
                      width: 0.25rem;
                      height: 0.5rem;
                      border: solid white;
                      border-width: 0 2px 2px 0;
                      transform: rotate(45deg);
                    }
                    
                    /* Pagination styling */
                    .pagination-button {
                      display: inline-flex;
                      align-items: center;
                      justify-content: center;
                      width: 2rem;
                      height: 2rem;
                      border-radius: 0.375rem;
                      font-size: 0.875rem;
                      font-weight: 500;
                      color: #4b5563;
                      background-color: white;
                      border: 1px solid #e5e7eb;
                      margin: 0 0.25rem;
                      transition: all 0.15s ease;
                    }
                    
                    .pagination-button:hover {
                      background-color: #f3f4f6;
                    }
                    
                    .pagination-button.active {
                      color: white;
                      background-color: #6366f1;
                      border-color: #6366f1;
                    }
                    
                    .pagination-button.disabled {
                      color: #9ca3af;
                      pointer-events: none;
                    }
                  `}</style>

                    <ExchangesList
                      exchanges={exchanges || []}
                      pagination={pagination}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      isLoading={isLoading}
                      selectedExchanges={selectedExchanges}
                      handleExchangeSelection={handleExchangeSelection}
                      refreshData={refreshExchangeData}
                      refreshDashboard={refreshStatusSummary}
                    />
                  </div>
                )}
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </div>

      {/* Hidden print components - Keep as is */}
      <div style={{ display: 'none' }}>
        {printingExchange && <PrintExchange exchangeId={printingExchange} />}
        {batchPrinting && selectedExchanges.length > 0 && (
          <BatchPrintExchanges exchangeIds={selectedExchanges.map(ex => ex.id)} />
        )}
      </div>

    </div>
  );
};

export default ExchangesPage;